import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { getAllowedAiModels } from '@/services/ai_models';

import { useTenantStore } from './tenant';

export const useModelStore = defineStore('modelStore', () => {
    type AiModelOptions = {
        model_id: string;
        model_label: string;
        is_free: boolean;
    };
    const tenantStore = useTenantStore();
    const aiModelOptions = ref<AiModelOptions[]>([]);
    const selectedAiModelId = ref<string>('');
    const loadError = ref<string | null>(null);
    const isLoading = ref(false);

    const loadAllowedModels = async (tenantId?: string) => {
        const resolvedTenantId = tenantId ?? tenantStore.tenantId ?? '';
        if (!resolvedTenantId) {
            aiModelOptions.value = [];
            selectedAiModelId.value = '';
            return;
        }

        loadError.value = null;
        isLoading.value = true;
        try {
            const models = await getAllowedAiModels(resolvedTenantId);
            aiModelOptions.value = models.map((model) => ({
                model_id: model.model_id,
                model_label: model.model_label,
                is_free: model.is_free ?? false
            }));
        } catch (e) {
            console.error('Failed to load allowed AI models:', e);
            loadError.value =
                e instanceof Error ? e.message : 'Failed to load models';
        } finally {
            isLoading.value = false;
        }
    };

    watch(
        () => tenantStore.tenantId,
        async (tenantId) => {
            await loadAllowedModels(tenantId || '');
        },
        { immediate: true }
    );

    watch(aiModelOptions, (newOptions) => {
        const currentStillValid = newOptions.some(
            (model) => model.model_id === selectedAiModelId.value
        );
        if (!currentStillValid && newOptions.length > 0) {
            selectedAiModelId.value = newOptions[0].model_id;
        } else if (newOptions.length === 0) {
            selectedAiModelId.value = '';
        }
    });

    const setSelectedModel = (modelId: string) => {
        if (aiModelOptions.value.some((model) => model.model_id === modelId)) {
            selectedAiModelId.value = modelId;
        } else {
            selectedAiModelId.value = aiModelOptions.value[0]?.model_id ?? '';
        }
    };

    const resetSelectedModel = () => {
        selectedAiModelId.value = aiModelOptions.value[0]?.model_id || '';
    };

    return {
        aiModelOptions,
        selectedAiModelId,
        loadError,
        isLoading,
        loadAllowedModels,
        setSelectedModel,
        resetSelectedModel
    };
});
